import { Configuration, PopupRequest, PublicClientApplication } from "@azure/msal-browser";

/**
 * Check if the current environment is a PR preview.
 * Matches Azure Static Web App preview environment patterns:
 * - https://{swa-name}-pr{number}.{region}.{version}.azurestaticapps.net
 * - https://{swa-name}-{namedEnv}.{region}.{version}.azurestaticapps.net
 */
export const isPrPreview = (): boolean =>
  /-(pr\d+|prsystempullrequ)\./.test(window.location.origin);

/**
 * Returns the redirect URI for MSAL authentication.
 *
 * For PR preview environments, uses the base SWA URL (without the PR suffix)
 * + /auth/redirect as a stable redirect URI registered in Entra ID.
 * A static HTML page at that path reads the hash fragment and redirects
 * back to the PR preview origin, where MSAL.js completes the token exchange.
 *
 * For non-PR environments, uses the current origin directly.
 */
const getRedirectUri = (): string => {
  const origin = window.location.origin;

  if (isPrPreview()) {
    // Strip the PR/environment suffix AND the region to get the production SWA URL.
    // Preview URLs include a region segment that production URLs don't have:
    //   Preview:    https://victorious-field-0deaf7603-prsystempullrequ.westeurope.4.azurestaticapps.net
    //   Production: https://victorious-field-0deaf7603.4.azurestaticapps.net
    const baseOrigin = origin
      .replace(/-(pr\d+|prsystempullrequ)/, "") // Remove PR suffix
      .replace(/\.westeurope\./, "."); // Remove region (preview URLs have it, production doesn't)
    return `${baseOrigin}/auth/redirect`;
  }

  return origin;
};

// Config object to be passed to Msal on creation
export const msalConfig: Configuration = {
  auth: {
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_ADAL_TENANT ?? ""}`,
    clientId: import.meta.env.VITE_ADAL_CLIENT_ID ?? "",
    redirectUri: getRedirectUri(),
    navigateToLoginRequestUrl: !isPrPreview(), // PR previews handle navigation via the redirect page
  },
};

// Add here scopes for id token to be used at MS Identity Platform endpoints.
export const loginRequest: PopupRequest = {
  scopes: [import.meta.env.VITE_OPENID_SCOPES ?? ""], //@Todo: Add the scopes from env variables
};

export const msalInstance = new PublicClientApplication(msalConfig);

export const getUserName = () => msalInstance.getAllAccounts()[0]?.name ?? "";

export const getTokenAsync = async () => {
  const activeAccount = msalInstance.getActiveAccount(); // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
  const accounts = msalInstance.getAllAccounts();

  if (!activeAccount && accounts.length === 0) {
    return msalInstance.loginRedirect();
  }
  const request = {
    ...loginRequest,
    account: activeAccount ?? accounts[0],
  };

  const authResult = await msalInstance.acquireTokenSilent(request);
  return authResult.accessToken;
};

export const getDecodedJWT = () => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  return msalInstance.getActiveAccount();
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export const logout = async () => msalInstance.logoutRedirect();
